/**
 * Die ⇄ card affordance mapping (HEX-133, ADR-0021 §1). Pure: given a rolled die
 * face and a card, what — if anything — does feeding that die to the card do? The
 * combat overlay reads this to glow the cards a picked-up die can feed, closing
 * the "which die feeds / bonuses which card" gap (e.g. Block +6 on a 1).
 *
 * Presentation-only: this restates the engine's predicate matching (`matches`)
 * for the *single-die* affordance cue; the engine remains the authority on what a
 * full assignment actually does (`canAccept` / `assignmentValid`).
 */

import { matches } from '../predicates';
import type { Card, DieFace } from '../types';

/** Why a die is linked to a card:
 *   - `bonus` — it pays a cost slot AND triggers a §19 cost-face bonus (the
 *     headline special, e.g. a 1 into Brace → +6 block). Only cost dice fire
 *     `costBonuses`, so a bonus die is always also a cost die.
 *   - `cost`  — it satisfies a plain cost predicate (no bonus on this face).
 *   - `extra` — it pays *no* cost slot but matches the §19 `extraCost.predicate`,
 *     so it can only ever be a scaling die.
 *
 * Paying a cost slot is the primary role, so a cost-capable face never reads as
 * `extra` even when `extraCost.predicate` is permissive (Power Strike's "any" —
 * a 6 there pays the `≥4` cost, it is not a scaling die). */
export type DieCardLink = 'bonus' | 'cost' | 'extra';

/** The strongest link a die `face` has to `card`, or `null` if the face is
 * irrelevant to it (pays no cost slot and is no scaling die). */
export function dieCardLink(face: DieFace, card: Card): DieCardLink | null {
  const isCost = card.cost.some((predicate) => matches(face, predicate));
  if (isCost) {
    if (card.costBonuses?.some((b) => matches(face, b.facePredicate))) return 'bonus';
    return 'cost';
  }
  if (card.extraCost && matches(face, card.extraCost.predicate)) return 'extra';
  return null;
}

/** Pool indices whose die face links to `card` (any reason). The overlay uses
 * this to enumerate the dice a card could take; per-die styling reads the reason
 * from {@link dieCardLink}. */
export function eligibleDiceForCard(card: Card, pool: ReadonlyArray<{ face: DieFace }>): number[] {
  const out: number[] = [];
  pool.forEach((die, index) => {
    if (dieCardLink(die.face, card) !== null) out.push(index);
  });
  return out;
}
